import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import {
  alignItems,
  strCode,
  releaseNoteOptions as releaseNoteOptionNames
} from "../../../../../consts";
import { t } from "../../../../../services";

import { LanguageSelectorTemplate } from "../../../../../templates";
import { FlexContainers, WrapperContainer } from "../../../../../components";

import { SimpleList } from "./sections/SimpleList/SimpleList";
import { ListWithIcons } from "./sections/ListWithIcons/ListWithIcons";
import { SummaryOfChanges } from "./sections/SummaryOfChanges/SummaryOfChanges";

import { ContentWrapper, MainWrapper, Title } from "./styled";

export const LocalizationConfig = ({
  releaseNoteOption,
  languageList,
  iconsData,
  thumbnailsData,
  setLanguageList,
  setIconsData,
  setThumbnailsData,
  missingFields = [],
  showErrors
}) => {
  const [selectedId, setSelectedId] = useState(0);
  const [activeConfig, setActiveConfig] = useState();
  const [configIds, setConfigIds] = useState([]);

  const [showErrorLangs, setShowErrorLangs] = useState([]);

  const errorLangsList = useMemo(() => {
    let errorLangs = missingFields;

    if (releaseNoteOption === releaseNoteOptionNames.SIMPLE_LIST) {
      errorLangs = errorLangs?.filter(
        (item) => item.newFeaturesList || item.improvementsList || item?.summary
      );
    }

    errorLangs = errorLangs.map((item) => item.language);

    return showErrors ? errorLangs : errorLangs.filter((lang) => showErrorLangs.includes(lang));
  }, [missingFields, showErrors, showErrorLangs, releaseNoteOption]);

  useEffect(() => {
    const newIconsData = iconsData?.filter((el) => configIds?.includes(el?.id)) ?? [];
    const newThumbnailsData = thumbnailsData?.filter((el) => configIds?.includes(el?.id)) ?? [];

    setIconsData(newIconsData);
    setThumbnailsData(newThumbnailsData);
  }, [configIds?.length, iconsData?.length, thumbnailsData?.length]);

  useEffect(() => {
    setShowErrorLangs([]);

    if (releaseNoteOption === releaseNoteOptionNames.SIMPLE_LIST) {
      setConfigIds([]);
    }
  }, [releaseNoteOption]);

  const configErrors = useMemo(() => {
    const selectedConfig = languageList.find((config) => config.id === selectedId);

    return missingFields?.find((item) => item.language === selectedConfig?.title);
  }, [selectedId, languageList, missingFields]);

  useEffect(() => {
    updateActiveConfig();
  }, [selectedId, languageList.length]);

  const updateActiveConfig = () => {
    const language = languageList.find((lang) => lang.id === selectedId);

    let acitveLanguageConfig = language?.config;

    if (!acitveLanguageConfig && configIds?.length) {
      acitveLanguageConfig = configIds.map((id) => {
        return { id, title: "", description: "" };
      });
    }

    setActiveConfig(acitveLanguageConfig);
  };

  const onChangeConfig = (config) => {
    const selectedLangIdx = languageList.findIndex((lang) => lang.id === selectedId);

    if (selectedLangIdx !== -1) {
      let newItems = [
        ...languageList.slice(0, selectedLangIdx),
        { ...languageList[selectedLangIdx], config },
        ...languageList.slice(selectedLangIdx + 1)
      ];

      if (Array.isArray(config)) {
        const configIds = config?.map((el) => el.id);
        setConfigIds(configIds);

        newItems = newItems.map((langList, idx) => {
          const langConfig = langList.config;

          const newLangConfig =
            idx === selectedLangIdx
              ? config
              : configIds.map((id) => {
                  const existingConfig = langConfig?.find((el) => el.id === id);

                  return existingConfig ?? { id, title: "", description: "" };
                });

          return { ...langList, config: newLangConfig };
        });
      }

      setLanguageList(newItems);
    }
  };

  const pushErrorLang = (language) => {
    setShowErrorLangs((prev) => [...prev, language]);
  };

  const removeErrorLang = (language) => {
    setShowErrorLangs((prev) => prev.filter((lang) => lang !== language));
  };

  const onChangeIconsData = (item) => {
    const idx = iconsData?.findIndex((el) => el?.id === item?.id);

    let newIconsData = [];

    if (idx === -1) {
      newIconsData = [...iconsData, item];
    } else {
      newIconsData = [...iconsData.slice(0, idx), item, ...iconsData.slice(idx + 1)];
    }

    setIconsData(newIconsData);
  };

  const onChangeThumbnailsData = (item) => {
    const idx = thumbnailsData?.findIndex((el) => el?.id === item?.id);

    let newIconsData = [];

    if (idx === -1 && item?.media) {
      newIconsData = [...thumbnailsData, item];
    } else if (item?.media) {
      newIconsData = [...thumbnailsData.slice(0, idx), item, ...thumbnailsData.slice(idx + 1)];
    } else {
      newIconsData = thumbnailsData.filter((el) => el.id !== item.id);
    }

    setThumbnailsData(newIconsData);
  };

  const onChangeSummary = (summary) => {
    const selectedLangIdx = languageList.findIndex((lang) => lang.id === selectedId);

    if (selectedLangIdx !== -1) {
      let newItems = [
        ...languageList.slice(0, selectedLangIdx),
        { ...languageList[selectedLangIdx], summary },
        ...languageList.slice(selectedLangIdx + 1)
      ];

      setLanguageList(newItems);
    }
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      width="100%">
      <Title>{t(strCode.LOCALISATION_TITLE)}</Title>
      <MainWrapper>
        <LanguageSelectorTemplate
          languageList={languageList}
          errorLangsList={errorLangsList}
          setLanguageList={setLanguageList}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          onAddLanguageCallback={updateActiveConfig}
          onEditLanguageCallback={updateActiveConfig}>
          <ContentWrapper>
            <SummaryOfChanges
              summaryOfChanges={languageList.find((l) => l.id === selectedId)?.summary}
              onChangeSummary={onChangeSummary}
              hasError={configErrors?.summary}
              showErrors={configErrors && errorLangsList.includes(configErrors?.language)}
            />

            {releaseNoteOption === releaseNoteOptionNames.SIMPLE_LIST ? (
              <SimpleList
                language={languageList.find((l) => l.id === selectedId)?.title}
                config={activeConfig}
                configErrors={configErrors}
                showErrors={configErrors && errorLangsList.includes(configErrors?.language)}
                languageListLength={languageList.length}
                onChange={onChangeConfig}
                pushErrorLang={pushErrorLang}
                removeErrorLang={removeErrorLang}
              />
            ) : (
              <ListWithIcons
                language={languageList.find((l) => l.id === selectedId)?.title}
                configList={activeConfig}
                iconsData={iconsData}
                thumbnailsData={thumbnailsData}
                configErrors={configErrors}
                showErrors={configErrors && errorLangsList.includes(configErrors?.language)}
                languageListLength={languageList.length}
                onChange={onChangeConfig}
                onChangeIconsData={onChangeIconsData}
                onChangeThumbnailsData={onChangeThumbnailsData}
                pushErrorLang={pushErrorLang}
                removeErrorLang={removeErrorLang}
              />
            )}
          </ContentWrapper>
        </LanguageSelectorTemplate>
      </MainWrapper>
    </WrapperContainer>
  );
};

LocalizationConfig.propTypes = {
  releaseNoteOption: PropTypes.string.isRequired,
  languageList: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string.isRequired })
  ).isRequired,
  iconsData: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, icon: PropTypes.object })
  ),
  thumbnailsData: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, media: PropTypes.object })
  ),
  setLanguageList: PropTypes.func.isRequired,
  setIconsData: PropTypes.func.isRequired,
  setThumbnailsData: PropTypes.func.isRequired,
  missingFields: PropTypes.arrayOf(
    PropTypes.shape({
      language: PropTypes.string.isRequired,
      newFeaturesList: PropTypes.string.isRequired,
      improvementsList: PropTypes.string.isRequired,
      showErrors: PropTypes.bool
    }).isRequired
  ),
  showErrors: PropTypes.bool.isRequired,
  setMissingFields: PropTypes.func.isRequired
};
